import React, { useMemo, useState } from 'react';

import { isValidEmail, Select } from '@aline/common';
import type { AutocompleteProps } from '@mui/material';
import { Autocomplete, ListItemText, Typography } from '@mui/material';
import { matchSorter } from 'match-sorter';
import {
  AccountAvatar,
  AccountChip,
  AlineAutocompleteInput,
  AlineAvatar,
  newUserEmailAvatarColor,
  Spacer,
} from 'src/components';
import { always, getEntityDisplayDetails } from 'src/helpers';
import type { AccountSubset, OrgSubset, UserSubset } from 'src/types';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  autocompleteOption: {
    padding: theme.spacing(1),
  },
}));

interface NewUserOption {
  __typename: 'NewUser';
  email: string;
}
export type UserOption = UserSubset | NewUserOption;
export type AccountOption = OrgSubset | UserOption;
type AccountsCustomAutocompleteProps = AutocompleteProps<AccountOption, true, false, false>;

export interface AccountsAutocompleteProps<T extends AccountSubset = AccountSubset> {
  accounts: T[];
  onChange: NonNullable<AccountsCustomAutocompleteProps['onChange']>;
  value: NonNullable<AccountsCustomAutocompleteProps['value']>;
  dataCy?: string;
  disabled?: boolean;
  filterPred?: (option: T) => boolean;
  getIsOptionDisabled?: (option: AccountOption) => boolean;
  maxOptions?: number;
  placeholder?: string;
}

export function AccountsAutocomplete<T extends AccountSubset>(
  props: AccountsAutocompleteProps<T>
): JSX.Element {
  const {
    accounts,
    dataCy,
    disabled,
    filterPred = always,
    getIsOptionDisabled = () => false,
    maxOptions,
    onChange,
    placeholder = 'Search accounts by name or email...',
    value,
  } = props;

  const { classes } = useStyles();
  const [autocompleteOpen, setAutocompleteOpen] = useState<boolean>(false);
  const filteredAccounts = useMemo(() => accounts.filter(filterPred), [accounts, filterPred]);

  return (
    <Autocomplete<AccountOption, true, false, false>
      filterSelectedOptions
      fullWidth
      multiple
      openOnFocus
      classes={{ option: classes.autocompleteOption }}
      data-cy={dataCy}
      disabled={disabled}
      filterOptions={(options, filterOptionsState) => {
        const userKeys: (keyof UserSubset)[] = ['displayName', 'email'];
        const userOptions = options.filter(
          (o): o is Extract<AccountOption, UserSubset> => o.__typename === 'User'
        );
        const sortedUserOptions = matchSorter<UserSubset>(
          userOptions,
          filterOptionsState.inputValue,
          {
            keys: userKeys,
          }
        );
        const orgKeys: (keyof OrgSubset)[] = ['name'];
        const orgOptions = options.filter(
          (o): o is Extract<AccountOption, OrgSubset> => o.__typename === 'Org'
        );
        const sortedOrgOptions = matchSorter<OrgSubset>(orgOptions, filterOptionsState.inputValue, {
          keys: orgKeys,
        });
        const userAlreadyExists = options.some(
          option =>
            option.__typename === 'User' &&
            option.email.toLowerCase().trim() === filterOptionsState.inputValue.toLowerCase().trim()
        );
        return filterOptionsState.inputValue && !userAlreadyExists
          ? [
              ...sortedOrgOptions,
              ...sortedUserOptions,
              {
                __typename: 'NewUser',
                email: filterOptionsState.inputValue.trim(),
              } satisfies NewUserOption,
            ]
          : options;
      }}
      getOptionDisabled={option => {
        const isInvalidEmail = option.__typename === 'NewUser' && !isValidEmail(option.email);
        return isInvalidEmail || getIsOptionDisabled(option);
      }}
      getOptionKey={option => {
        switch (option.__typename) {
          case 'NewUser':
            return option.email;
          case 'User':
          case 'Org':
            return option.id;
        }
      }}
      getOptionLabel={option => {
        switch (option.__typename) {
          case 'NewUser':
            return option.email;
          case 'User':
          case 'Org': {
            const { primary } = getEntityDisplayDetails(option);
            return primary;
          }
        }
      }}
      isOptionEqualToValue={(option: AccountOption, value: AccountOption) => {
        if (option.__typename === 'Org' || value.__typename === 'Org')
          return 'id' in option && 'id' in value && option.id === value.id;
        return option.email.toLowerCase() === value.email.toLowerCase();
      }}
      open={autocompleteOpen}
      options={filteredAccounts}
      renderInput={params => (
        <AlineAutocompleteInput aria-label="Accounts" placeholder={placeholder} {...params} />
      )}
      renderOption={(props, option) => {
        const { key, ...optionProps } = props;

        return (
          <li key={key} {...optionProps}>
            <AccountOption option={option} />
          </li>
        );
      }}
      renderValue={(options, getItemProps) => {
        return options.map((option, index) => {
          const { key, ...tagProps } = getItemProps({ index });
          return <AccountChip key={key} accountInfo={option} {...tagProps} />;
        });
      }}
      slotProps={{
        listbox: {
          'data-cy': Select.components.accountsAutocomplete.listBox,
        },
      }}
      value={value}
      onChange={(event, values, reason, details) => {
        const adjustedValues = typeof maxOptions === 'number' ? values.slice(-maxOptions) : values;
        onChange(event, adjustedValues, reason, details);
        setAutocompleteOpen(false);
      }}
      onClose={(): void => setAutocompleteOpen(false)}
      onOpen={(): void => setAutocompleteOpen(true)}
    />
  );
}

const useOptionStyles = makeStyles()(theme => ({
  newUser: {
    ...theme.appTypography.buttonSmall,
    color: theme.appTypography.buttonSmall.color,
    whiteSpace: 'nowrap',
  },
  option: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
    width: '100%',
  },
  secondaryText: {
    color: theme.appColors.secondaryType,
    fontFamily: theme.typography.fontFamily,
    fontSize: 12,
  },
}));

interface AccountOptionProps {
  option: AccountOption;
}

function AccountOption({ option }: AccountOptionProps): JSX.Element {
  const { classes } = useOptionStyles();

  switch (option.__typename) {
    case 'NewUser': {
      return (
        <div className={classes.option}>
          <AlineAvatar
            avatarInfo={option.email.charAt(0).toUpperCase()}
            styles={{
              backgroundColor: newUserEmailAvatarColor,
              size: 'lg',
            }}
          />
          <Typography noWrap>{option.email}</Typography>
          <Spacer />
          <Typography className={classes.newUser}>Assign email</Typography>
        </div>
      );
    }
    default: {
      const { primary, secondary } = getEntityDisplayDetails(option);

      return (
        <div
          className={classes.option}
          data-cy={Select.components.accountsAutocomplete.accountOption}
        >
          <AccountAvatar account={option} size="lg" />
          <ListItemText
            classes={{ secondary: classes.secondaryText }}
            data-cy="user-option-text"
            primary={primary}
            secondary={secondary}
            slotProps={{
              primary: { align: 'left', noWrap: true, variant: 'body1' },
              secondary: { align: 'left', noWrap: true },
            }}
          />
        </div>
      );
    }
  }
}
